import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const ref = searchParams.get('ref') || '';
  const refcode = searchParams.get('refcode') || '';

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isPasswordValid = hasMinLength && hasUpperCase && hasSpecialChar;
  const isFormValid = email !== '' && password !== '' && isPasswordValid;

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setLoading(true);

    try {
      const userPayload = await register(email, password, ref, refcode);
      if (userPayload) {
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (err) {
      const errorMessage = err.message || 'Erro ao criar conta. Tente novamente.';
      setEmailError(errorMessage);
      console.error('[Register] Erro no formulário:', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = isFormValid && !loading && !emailError;

  if (success) {
    return (
      <div className="bg-[#012B87] bg-fixed min-h-screen">
        <div className="max-w-[392px] mx-auto h-[90vh] flex items-center justify-center px-4 lg:px-0">
          <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-6 py-10 text-center shadow-lg">
            <i className="bi-check-circle text-green-500 text-5xl mb-4"></i>
            <h1 className="text-xl md:text-2xl font-semibold mb-3">Conta criada com sucesso!</h1>
            <p className="text-sm text-gray-600 mb-6">
              Estamos levando você para o seu dashboard Stackfy.
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
            >
              Ir para o dashboard
              <i className="bi-arrow-right-short text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#012B87] bg-fixed min-h-screen">
      <div className="max-w-[392px] mx-auto h-[90vh] flex items-center justify-center px-4 lg:px-0">
        <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-5 py-7 sm:px-8 sm:py-9 md:px-10 md:py-11">
          <h1 className="text-lg md:text-2xl font-semibold mb-4 md:mb-5">Crie sua conta</h1>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-slate-700 text-sm">
            <div
              className={`transition-all duration-500 border rounded flex items-center gap-3 sm:gap-4 py-2 px-4 h-[50px] ${
                email ? 'border-[#2C1944]' : 'border-gray-300'
              }`}
            >
              <i className="bi-envelope transition-all duration-500 text-slate-400 text-lg sm:text-xl"></i>
              <div className="flex-1">
                <input
                  type="text"
                  id="emailInput"
                  className="w-full bg-transparent outline-none"
                  spellCheck="false"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                />
              </div>
            </div>
            {emailError && <div className="text-xs text-red-600 -mt-2 -mb-1">{emailError}</div>}

            <div
              className={`transition-all duration-500 border rounded flex items-center justify-between gap-3 sm:gap-4 py-2 px-4 h-[50px] ${
                password ? 'border-[#2C1944]' : 'border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 sm:gap-4 flex-1">
                <i className="bi-lock transition-all duration-500 text-slate-400 text-lg sm:text-xl"></i>
                <div className="flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="passwordInput"
                    className="w-full bg-transparent outline-none"
                    spellCheck="false"
                    placeholder="Senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <i
                onClick={() => setShowPassword(!showPassword)}
                className={`${showPassword ? 'bi-eye' : 'bi-eye-slash'} text-slate-400 text-lg sm:text-xl cursor-pointer`}
              ></i>
            </div>

            <div className="text-xs text-gray-400 mt-2">
              <p>Sua senha deve possuir:</p>
              <ul className="mt-2 sm:mt-3">
                <li className="flex items-center gap-2">
                  <i className={`bi-check2 text-base ${hasMinLength ? 'text-green-500' : 'text-gray-400'}`}></i>
                  No mínimo 8 caracteres
                </li>
                <li className="flex items-center gap-2">
                  <i className={`bi-check2 text-base ${hasUpperCase ? 'text-green-500' : 'text-gray-400'}`}></i>
                  1 letra maiúscula
                </li>
                <li className="flex items-center gap-2">
                  <i className={`bi-check2 text-base ${hasSpecialChar ? 'text-green-500' : 'text-gray-400'}`}></i>
                  1 caractere especial
                </li>
              </ul>
            </div>

            <input
              type="submit"
              value={loading ? 'Criando conta...' : 'Entrar'}
              className={`submitBtn transition-all w-full rounded py-2 cursor-pointer font-semibold ${
                canSubmit
                  ? 'bg-tBlue hover:bg-tBlue2 text-white'
                  : 'bg-gradient-to-r from-[#EBEBEB] to-[#EBEBEB] text-slate-400 cursor-not-allowed'
              }`}
              disabled={!canSubmit}
            />

            <div className="flex items-center gap-1 text-xs mt-2">
              Ja possui uma conta?
              <Link to="/login" className="font-semibold text-xs sm:text-sm hover:underline ml-1 flex items-center">
                Fazer login <i className="bi-arrow-right-short text-tOrange text-xl mt-1 ml-1"></i>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
